import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { DataSourceType, DataSourceState, DataSourceDependency } from '../interfaces';
import { ContextModelService } from './context-model.service';
import { DATA_SOURCE_DEPENDENCIES } from '../data/data-source-dependencies';
import { DataSourcesService, DataSourceItem } from '../../v-api';

@Injectable({
    providedIn: 'root'
})
export class DataSourceService {
    private readonly dataSourcesService = inject(DataSourcesService);
    private readonly contextModelService = inject(ContextModelService);

    private readonly _dataSourcesCalled$: BehaviorSubject<number> = new BehaviorSubject(0);
    private _dataSourcesStates: { [key: string]: BehaviorSubject<DataSourceState> } = {};

    private _dataSourceDependencies: DataSourceDependency;
    private _dataSourceDependenciesLoaded = false;

    get dataSourcesCalled$(): Observable<number> {
        return this._dataSourcesCalled$.asObservable();
    }

    getDataSourceState(dataSource: DataSourceType): Observable<DataSourceState> {
        return this._dataSourcesStates[dataSource].asObservable();
    }

    getDataSourceItems(dataSource: DataSourceType): Observable<DataSourceItem[]> {
        if (!dataSource) {
            throw new Error('DataSource is not defined for the given slot');
        }

        if (!this._dataSourcesStates[dataSource]) {
            this._dataSourcesStates[dataSource] = new BehaviorSubject<DataSourceState>({
                dataSource: { items: [] },
                loading: true,
                loaded: false
            });
        } else {
            this._dataSourcesStates[dataSource].next({ ...this._dataSourcesStates[dataSource].value, loading: true });
        }

        return this.getDataSourceDependencies().pipe(
            switchMap(dependencies => {
                const currentState = this.contextModelService.currentState;
                const body = this.buildPayloadBody(dataSource, dependencies, currentState);
                return this.dataSourcesService.getDataSources({ dataSource, ...body }).pipe(
                    map(response => response.items),
                    tap(items => {
                        this._dataSourcesStates[dataSource].next({
                            dataSource: { items },
                            loading: false,
                            loaded: true
                        });
                        const _dataSource = this.contextModelService.getDataSource(dataSource);
                        if (!_dataSource) {
                            this.contextModelService.setDataSource(dataSource, { items });
                        } else {
                            this.contextModelService.setDataSource(dataSource, { ..._dataSource, items });
                        }
                        this._dataSourcesCalled$.next(this._dataSourcesCalled$.value + 1);
                    }),
                    catchError(() => {
                        this._dataSourcesStates[dataSource].next({
                            dataSource: { items: [] },
                            loading: false,
                            loaded: false,
                            error: 'Error loading data'
                        });
                        return [];
                    })
                );
            })
        );
    }

    private buildPayloadBody(
        dataSource: DataSourceType,
        dependencies: DataSourceDependency,
        currentState: unknown
    ): Record<string, unknown> {
        const body: Record<string, unknown> = {};

        if (!currentState || !dependencies) {
            return body;
        }

        Object.entries(dependencies).forEach(([path, dependentDataSources]) => {
            if (!dependentDataSources?.includes(dataSource)) {
                return;
            }

            const value = this.getValueFromStateByPath(currentState, path);
            if (value === undefined) {
                return;
            }

            const key = path.includes('.') ? path.split('.').pop()! : path;
            body[key] = value;
        });

        return body;
    }

    private getValueFromStateByPath(state: unknown, path: string): unknown {
        return path.split('.').reduce<unknown>((acc: unknown, segment: string) => {
            if (acc && typeof acc === 'object' && segment in (acc as Record<string, unknown>)) {
                return (acc as Record<string, unknown>)[segment];
            }
            return undefined;
        }, state);
    }

    getDataSourceDependencies(): Observable<DataSourceDependency> {
        if (this._dataSourceDependenciesLoaded && this._dataSourceDependencies) {
            return of(this._dataSourceDependencies);
        }

        // TODO: Replace with DataControllerDataSource service Http GET method when API endpoint is available
        return of(DATA_SOURCE_DEPENDENCIES).pipe(
            tap(dependencies => {
                this._dataSourceDependencies = dependencies;
                this._dataSourceDependenciesLoaded = true;
            })
        );
    }
}
